import { CompanyPosition } from '@vyce/core/src/types';

import { getResourceAliases } from './resourceKeys';

// The sets on a position are already effective — role defaults with the module and
// per-resource layers folded in. Never re-derive anything from role names.
//
// owner: true short-circuits everything, and an owner's `resources` is {} on
// purpose — so check owner first or owners lose access to every row.
//
// Everything here is fail-closed: no position, or no data, means no.

const listFor = (position: CompanyPosition, resourceType: string, resourceId: string): string[] => {
  const resources = position.resources;
  if (!resources) return [];
  for (const alias of getResourceAliases(resourceType)) {
    const byId = resources[alias];
    const actions = byId?.[resourceId];
    if (actions) return actions;
  }
  return [];
};

const entriesFor = (position: CompanyPosition, resourceType: string): [string, string[]][] => {
  const resources = position.resources;
  if (!resources) return [];
  const entries: [string, string[]][] = [];
  getResourceAliases(resourceType).forEach(alias => {
    Object.entries(resources[alias] || {}).forEach(entry => entries.push(entry));
  });
  return entries;
};

/** Every resource type the backend scopes a permission to. */
export const RESOURCE_TYPES = ['location', 'team', 'pay_schedule'];

/**
 * Company-wide rights. `permissions` is the only source — company_actions is
 * gone, and everything it used to carry now arrives in this flat array.
 */
export const hasCompanyPermission = (position: CompanyPosition | undefined, permission: string): boolean => {
  if (!position) return false;
  if (position.owner) return true;
  return (position.permissions as string[] | undefined)?.includes(permission) || false;
};

export const hasAnyCompanyPermission = (
  position: CompanyPosition | undefined,
  permissions: string[]
): boolean => permissions.some(permission => hasCompanyPermission(position, permission));

/**
 * Per-row rights: the manual clock-in button can be on for one location and off
 * for another, for the same person under the same role. A missing key means
 * "nothing of this type", not an error.
 *
 * `resources` only ever *widens* what the position already grants — a right held
 * company-wide covers every resource of that type and is usually absent from the
 * map. So check the company level too, or someone holding time:can_update_sites
 * for the whole company loses the Edit button on every single location.
 */
export const hasResourcePermission = (
  position: CompanyPosition | undefined,
  permission: string,
  resourceType: string,
  resourceId?: string
): boolean => {
  if (!position) return false;
  if (position.owner) return true;
  if (hasCompanyPermission(position, permission)) return true;
  if (!resourceId) return false;
  return listFor(position, resourceType, resourceId).includes(permission);
};

/**
 * For screens that aggregate every resource and so have no id to check against —
 * the by-period time logs, or deciding whether a whole tab is reachable.
 */
export const hasPermissionOnAnyResource = (
  position: CompanyPosition | undefined,
  permission: string,
  resourceType: string
): boolean => {
  if (!position) return false;
  if (position.owner) return true;
  if (hasCompanyPermission(position, permission)) return true;
  return entriesFor(position, resourceType).some(([, actions]) => actions?.includes(permission));
};

/**
 * Held company-wide or on at least one resource of any type. The gate for
 * anything list-shaped — a screen, a tab, or a request that returns rows the
 * backend filters itself. A manager scoped to a single location holds their
 * rights only inside `resources`, so a company-only check hides the whole
 * screen from them.
 */
export const hasPermissionAnywhere = (position: CompanyPosition | undefined, permission: string): boolean =>
  hasCompanyPermission(position, permission) ||
  RESOURCE_TYPES.some(resourceType => hasPermissionOnAnyResource(position, permission, resourceType));

/**
 * The resource ids this person holds the permission on, for filtering an option
 * list. `null` means "all of them" — an owner carries no resources map at all,
 * so there is nothing to filter against.
 */
export const getResourceIdsWithPermission = (
  position: CompanyPosition | undefined,
  permission: string,
  resourceType: string
): string[] | null => {
  if (!position) return [];
  // Company-wide, like an owner, means "all of them" — there is nothing to filter.
  if (position.owner || hasCompanyPermission(position, permission)) return null;
  return entriesFor(position, resourceType)
    .filter(([, actions]) => actions?.includes(permission))
    .map(([resourceId]) => resourceId);
};

/**
 * Whether a position grants anything at all in the employer interface.
 *
 * Not `!!position.permissions?.length`: an owner arrives with an empty array on
 * purpose, and so does a manager scoped purely to locations, teams or pay
 * schedules — their rights live in `resources`.
 */
export const isEffectiveAdminPosition = (position?: CompanyPosition): boolean =>
  !!position &&
  (position.owner ||
    !!position.permissions?.length ||
    Object.values(position.resources || {}).some(byId => Object.keys(byId || {}).length > 0));

/**
 * A 403 is the server's authoritative "you may not", not a failure. Callers that
 * can degrade gracefully (hide a widget, skip a section) use this instead of
 * reporting a server error.
 */
export const isForbiddenError = (error: any): boolean => error?.response?.status === 403;
